import { useCallback, useEffect, useRef, useState } from 'react'
import {
  checkProspectDuplicates,
  findSimilarProspectNames,
  hasMeaningfulIdentity,
  type ProspectIdentityValues,
} from '../../lib/prospectDuplicateCheck'
import type { CrmRecord } from '../../lib/crm'

export type DuplicateCheckStatus =
  | 'idle'
  | 'checking'
  | 'available'
  | 'exact'
  | 'similar'
  | 'error'

export interface ProspectDuplicateState {
  status: DuplicateCheckStatus
  exactMatches: CrmRecord[]
  similarMatches: CrmRecord[]
  message: string | null
}

const initialState: ProspectDuplicateState = {
  status: 'idle',
  exactMatches: [],
  similarMatches: [],
  message: null,
}

export function useProspectDuplicateCheck(
  identity: ProspectIdentityValues,
  excludeProspectId: string | null = null,
) {
  const [state, setState] = useState<ProspectDuplicateState>(initialState)
  const requestRef = useRef(0)

  const validate = useCallback(async () => {
    if (!hasMeaningfulIdentity(identity)) {
      setState(initialState)
      return
    }

    const requestId = requestRef.current + 1
    requestRef.current = requestId
    setState((current) => ({ ...current, status: 'checking', message: null }))

    const exact = await checkProspectDuplicates(identity, excludeProspectId)
    if (requestId !== requestRef.current) return

    if (exact.error) {
      setState({
        status: 'error',
        exactMatches: [],
        similarMatches: [],
        message: exact.error,
      })
      return
    }

    if (exact.records.length) {
      setState({
        status: 'exact',
        exactMatches: exact.records,
        similarMatches: [],
        message: null,
      })
      return
    }

    const similar = await findSimilarProspectNames(
      identity.businessName,
      excludeProspectId,
    )
    if (requestId !== requestRef.current) return

    setState({
      status: similar.error
        ? 'error'
        : similar.records.length
          ? 'similar'
          : 'available',
      exactMatches: [],
      similarMatches: similar.records,
      message: similar.error,
    })
  }, [
    excludeProspectId,
    identity.businessName,
    identity.email,
    identity.facebook,
    identity.googleMaps,
    identity.instagram,
    identity.phone,
    identity.website,
    identity.whatsapp,
  ])

  useEffect(() => {
    if (!hasMeaningfulIdentity(identity)) {
      setState(initialState)
      return undefined
    }

    const timer = window.setTimeout(() => {
      void validate()
    }, 500)

    return () => window.clearTimeout(timer)
  }, [identity, validate])

  return { ...state, refresh: validate }
}
